/**
 * DD-AMZ-BU-001 — multi-value attributes.
 *
 * Line attributes are one string per key (`line_attributes`, any string ≤ 2000 chars on the
 * API), so a multi-select field stores its picks joined with "; " and every reader splits
 * them back. Legacy single values ("AFO | Amazon Fresh Online", the old PIM BU labels) are
 * matched to the current option by their leading code so old lines show — and re-save as —
 * the current wording; a part with no match is kept verbatim (never silently dropped).
 */
export const MULTI_SEPARATOR = '; '

export function splitMulti(value: string | null | undefined): string[] {
  if (typeof value !== 'string') return []
  const out: string[] = []
  for (const raw of value.split(';')) {
    const part = raw.trim()
    if (part && !out.includes(part)) out.push(part)
  }
  return out
}

export function joinMulti(parts: readonly string[]): string {
  const out: string[] = []
  for (const raw of parts) {
    const part = raw.trim()
    if (part && !out.includes(part)) out.push(part)
  }
  return out.join(MULTI_SEPARATOR)
}

/** "AFO — Amazon Fresh Online" / "AFO | Amazon Fresh Online" / "AFO" → "afo". */
function leadingCode(text: string): string {
  const m = /^(.+?)\s*(?:—|–|\||:|\s-\s)/u.exec(text.trim())
  return (m ? m[1] : text).trim().toLowerCase()
}

/**
 * Map each stored part onto the option it means: exact match first, then by leading code
 * (case-insensitive). Unmatched parts are returned as they are.
 */
export function canonicalizeParts(parts: readonly string[], options: readonly string[]): string[] {
  if (options.length === 0) return [...parts]
  const byCode = new Map<string, string>()
  for (const opt of options) {
    const code = leadingCode(opt)
    if (!byCode.has(code)) byCode.set(code, opt)
  }
  const out: string[] = []
  for (const part of parts) {
    const hit = options.includes(part) ? part : (byCode.get(leadingCode(part)) ?? part)
    if (!out.includes(hit)) out.push(hit)
  }
  return out
}

/** Stored string → display / form string with legacy parts mapped to the current options. */
export function canonicalizeMulti(
  value: string | null | undefined,
  options: readonly string[]
): string {
  return joinMulti(canonicalizeParts(splitMulti(value), options))
}
